import { AnimatePresence, motion } from "framer-motion";
import React, { useContext, useEffect, useState } from "react";
import { Button, Dropdown, Spinner } from "react-bootstrap";
import { BiSolidCloudDownload } from "react-icons/bi";
import { FaChevronDown, FaChevronRight, FaFilter, FaLink, FaSearch, FaTimes } from "react-icons/fa";
import { FaCheckToSlot } from "react-icons/fa6";
import { MdErrorOutline, MdOutlineScheduleSend } from "react-icons/md";
import { AuditObjectTypes } from "../../_core/_model.ts";
import { AuthContext } from "../../context/AuthContext.js";
import { ThemeContext } from "../../context/ThemeContext.js";
import { defaultTheme } from "../../styles/themes/defaultTheme.js";
import ConfirmDialog from "../../UIComponents/ConfirmDialog.tsx";
import DeleteRowButton from "../../UIComponents/DeleteRowButton.tsx";
import { AreObjectsEquals, getMyLocalTime } from "../../Utilities/Utilities.ts";
import ComponentLayout from "../Layouts/ComponentLayout.tsx";
import TooltipButton from "../Tools/TooltipButton.tsx";
import { ReportModel } from "./_core/_model.ts";
import { DeleteReport, GetPowerBIReport, GetReport, RetryReport, UpsertReport } from "./_core/_request.ts";
import SetDistributionModal from "./SetDistributionModal.tsx";
import SearchTextbox from "../../UIComponents/SearchTextbox.tsx";
import { RxCross2 } from "react-icons/rx";
import SearchAndAdvancedFilters, { appliedFilterType } from "../Tables/SearchAndAdvancedFilters.tsx";
import { TbStatusChange } from "react-icons/tb";
import { string } from "yup";
import { useToast } from "../../context/ToastContext.tsx";
import ReportColumnRestrictions from "./ColumnRestrictions/ReportColumnRestrictions.tsx";
import { FiShield } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

export default function TreeReportView() {
    const [reports, setReports] = useState<ReportModel[] | null>(null);
    const [copiedID, setCopiedID] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [selectedReport, setSelectedReport] = useState<ReportModel | null>(null);
    const [isFetchingReports, setIsFetchingReports] = useState(false);
    const [fetchedCount, setFetchedCount] = useState(0);
    const [powerBIList, setPowerBIList] = useState<ReportModel[]>([]);
    const [confirmToggle, setConfirmToggle] = useState<number | null>(null);
    const { themeConfig } = useContext(ThemeContext);
    const { user } = useContext(AuthContext);
    const { showToast } = useToast();
    const [filterApplied, setFilterApplied] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (powerBIList.length > 0 && !powerBIList.find(x => x.processing)) {
            const [current, ...rest] = powerBIList;
            current.processing = true;
            setPowerBIList([current, ...rest]);
            setTimeout(() => {
                current.createdBy = user.userID;
                UpsertReport(current).then(() => {
                    setPowerBIList(rest);
                });
            }, 500);
        } else {
            initReports();
        }
    }, [powerBIList]);

    const initReports = () => {
        GetReport({ emailId: null, userID: null }).then(res => {
            setReports([...res.data]);
            setConfirmToggle(null);
            if (res.data.length > 0) {
                let wks = { [res.data[0].workspaceName]: true };
                setExpanded(wks);
            }
        });
    };

    const fetchPowerBIReports = () => {
        setIsFetchingReports(true);
        setFetchedCount(0);
        GetPowerBIReport({}).then((x) => {
            let rArr = x.data.filter((f) => f.workspaceName == "Reports");
            const tArr: ReportModel[] = rArr.map((item: ReportModel) => ({
                ...item,
                processing: false,
                createdBy: user.userID
            }));
            setFetchedCount(tArr.length);
            setPowerBIList([...tArr]);
            setIsFetchingReports(false);
        });
    };

    const groupByWorkspace = (reports: ReportModel[]) => {
        const result: Record<string, ReportModel[]> = {};
        for (const rpt of reports) {
            const key = rpt.workspaceName || "Unknown Workspace";
            if (!result[key]) result[key] = [];
            result[key].push(rpt);
        }
        return result;
    };

    const toggleExpand = (workspace: string) => {
        let prev = expanded;
        prev[workspace] = !prev[workspace];
        setExpanded({ ...prev });
    };

    const handleToggleStatus = (indx: number, report: ReportModel) => {
        if (reports != null) {
            let tArr = reports;
            tArr[indx].progress = true;
            setReports([...tArr]);
            const updated = { ...report, isActive: report.isActive ? 0 : 1 };
            updated.createdBy = user.userID;
            UpsertReport(updated).then(initReports);
        }
    };
    const [retryProgress, setRetryProgress] = useState<boolean>(false);

    const handleRetry = (report: ReportModel) => {
        setRetryProgress(true);
        RetryReport({ reportAutoID: report.reportAutoID, reportID: report.reportID, workspaceID: report.workspaceID }).then((x) => {
            if (x.data == "") {
                showToast({
                    message: "Report successfully restored. It will be delivered during the next scheduled distribution.",
                    type: "success",
                    duration: 5000
                });
                let rpts = reports;
                if (rpts != null) {
                    let rptIndx = rpts.findIndex((r) => r.reportID == report.reportID);
                    if (rptIndx > -1) {
                        rpts[rptIndx].errorMsg = "";
                        setReports([...rpts]);
                    }
                }
            }
            else {
                showToast({
                    message: x.data,
                    type: "error",
                    duration: 5000
                })
            }
            setRetryProgress(false);
        })
    }

    const handleDelete = (report: ReportModel) => {
        DeleteReport(report.reportID, user.userID).then(initReports);
    };
    const defaultFilter = {
        name: "",
        unscheduled: false,
        includeDisabled: false,
        scheduleDay: ""
    };

    const [appliedFilters, setAppliedFilters] = useState<{
        name: string | null,
        unscheduled: boolean,
        includeDisabled: boolean,
        scheduleDay: string
    }>(defaultFilter);

    const filteredReports = (reports != null) ? reports.filter(rpt =>
        (appliedFilters.name == null || appliedFilters.name == "" || rpt.reportName.toLowerCase().includes(appliedFilters.name.toLowerCase())
            || rpt.workspaceName.toLowerCase().includes(appliedFilters.name.toLowerCase())) &&
        (rpt.isActive == 1 || (rpt.isActive == 0 && appliedFilters.includeDisabled)) &&
        ((typeof rpt.isScheduled == 'undefined' || rpt.isScheduled) || (!rpt.isScheduled && appliedFilters.unscheduled))
    ) : [];

    const grouped = groupByWorkspace(filteredReports);
    const DotSpinner = () => (
        <div className="d-flex gap-1 align-items-center">
            {[0, 1, 2].map((i) => (
                <motion.div
                    key={i}
                    className="rounded-circle"
                    style={{
                        width: 6,
                        height: 6,
                        backgroundColor: defaultTheme.colors.primary,
                    }}
                    animate={{
                        opacity: [0.3, 1, 0.3],
                    }}
                    transition={{
                        repeat: Infinity,
                        duration: 1.2,
                        ease: "easeInOut",
                        delay: i * 0.2,
                    }}
                />
            ))}
        </div>
    );

    const generateReportUrl = (report: ReportModel) => {
        return report.webUrl;
    };


    const handleCopyLink = (report: ReportModel) => {
        const url = generateReportUrl(report);
        navigator.clipboard.writeText(url).then(() => {
            setCopiedID(report.reportID);
            setTimeout(() => setCopiedID(null), 3000);
        });
    };

    return (
        <ComponentLayout
            exButtons={<div>
                {isFetchingReports && (
                    <div className="d-flex align-items-center gap-2 small text-success">
                        <DotSpinner />
                        <span>Fetching reports...</span>
                    </div>
                )}
                <div className="d-flex flex-row align-items-center gap-2">
                    {!isFetchingReports && powerBIList.length > 0 && powerBIList[0]?.processing && (
                        <div className="d-flex align-items-center gap-2 text-success small">
                            <DotSpinner />
                            <div className="d-flex flex-column">
                                <div style={{ fontSize: "13px" }}>
                                    Importing:{" "}
                                    <AnimatePresence mode="wait">
                                        <motion.span
                                            key={powerBIList[0]?.reportName}
                                            initial={{ opacity: 0, y: -5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 5 }}
                                            transition={{ duration: 0.25 }}
                                            className="text-success fw-bold"
                                        >
                                            {powerBIList[0]?.reportName}
                                        </motion.span>
                                    </AnimatePresence>
                                </div>
                                <div className="text-muted-theme small" style={{ fontSize: "12px" }}>
                                    {fetchedCount - powerBIList.length + 1} of {fetchedCount}
                                </div>
                            </div>
                        </div>
                    )}
                    <TooltipButton title="Click to fetch Reports from Power BI">
                        <button
                            className="btn btn-outline-success d-flex align-items-center gap-2 px-3 j_rep_fetch"
                            onClick={fetchPowerBIReports}
                            disabled={isFetchingReports}
                        >
                            <BiSolidCloudDownload size={18} />
                            <span style={{ fontSize: "13px" }}>
                                {isFetchingReports ? "Fetching..." : "Fetch Reports"}
                            </span>
                        </button>
                    </TooltipButton>
                    <TooltipButton title="Click to manage restricted columns for reports">
                        <button
                            className="btn btn-outline-success d-flex align-items-center gap-2 px-3 j_rep_fetch"
                            onClick={() => {
                                navigate("/columnrestrictions");
                            }}
                            disabled={isFetchingReports}
                        >
                            <FiShield size={18} />
                            <span style={{ fontSize: "13px" }}>
                                Report Column Restrictions
                            </span>
                        </button>
                    </TooltipButton>
                </div>
            </div>}
            instanceId={0} objectType={AuditObjectTypes.Report}
            title={
                <div className="d-flex flex-row align-items-center justify-content-between w-100 gap-2 me-2">
                    <div className="d-flex align-items-center gap-3">
                        <span>Report List</span>
                    </div>
                </div>
            }
            bodyHeight={400}
            subHeader={
                <div className='d-flex flex-column w-100'>
                    <SearchAndAdvancedFilters {...{
                        exButton: undefined,
                        initialValues: { "": null },
                        onChange: (filters, afterLoad) => {
                            let unscheduled = (filters?.Status != null) ? filters.Status.indexOf("Unscheduled") > -1 : false;
                            let disabled = (filters?.Status != null) ? filters.Status.indexOf("Disabled") > -1 : false;
                            setFilterApplied(true);
                            setAppliedFilters({ unscheduled: unscheduled, includeDisabled: disabled, scheduleDay: "", name: filters.name });
                            afterLoad([]);
                            //setGlobalFilter(e.target.value)
                            // getRows({
                            //     roleID: filters.roleID, roleGroupName: filters.name,
                            //     roleGroupID: filters.roleGroupID, userID: filters.userID
                            // }, afterLoad);
                        },
                        filters: {
                            "Status": {
                                exValues: [{ key: "Unscheduled", value: "Unscheduled Reports" }, { key: "Disabled", value: "Disabled Reports" }],
                                filterType: appliedFilterType.Dropdown,
                                icon: <TbStatusChange />,
                                label: "Status"
                            }
                        },
                        values: {
                            "roleID": null,
                            "userID": null
                        },
                    }} />
                </div>
            }
            cBody={
                <div className="py-3 px-4" style={{ maxHeight: "100%", overflow: "auto" }}>
                    {
                        reports == null ?
                            <div className="w-100 d-flex align-items-center justify-content-center" style={{ height: "300px" }}>
                                <div className="d-flex align-items-center gap-2 small text-success">
                                    <Spinner />
                                    <span>Fetching reports...</span>
                                </div></div> :
                            filteredReports.length == 0 ?
                                <AnimatePresence>
                                    <motion.div
                                        className="d-flex flex-column align-items-center justify-content-center text-muted-theme p-5 m-4 bg-gray"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, ease: "easeOut" }}
                                        style={{
                                            border: "2px dashed #dee2e6",
                                            borderRadius: "10px",
                                            marginTop: "40px",
                                        }}
                                    >
                                        <FaCheckToSlot size={60} color="#adb5bd" className="mb-3" />
                                        <h5 className="fw-semibold">No Report Found</h5>
                                        {
                                            (!AreObjectsEquals(defaultFilter, appliedFilters)) ?
                                                <p className="text-center" style={{ maxWidth: 400 }}>
                                                    No Report found for selected filter.
                                                </p> :
                                                <p className="text-center" style={{ maxWidth: 400 }}>
                                                    It looks like there are no Reports added yet. Click{" "}
                                                    <strong>”Fetch Reports”</strong> to Fetch reports from Power BI.
                                                </p>
                                        }
                                    </motion.div>
                                </AnimatePresence> :
                                Object.entries(grouped).map(([workspace, reports]) => (
                                    <div key={workspace} className="mb-3 rounded shadow-sm">
                                        <div
                                            className="d-flex align-items-start justify-content-between card px-3 py-2"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => { setFilterApplied(false); toggleExpand(workspace); }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                {(expanded[workspace] || !AreObjectsEquals(defaultFilter, appliedFilters)) ? <FaChevronDown /> : <FaChevronRight />}
                                                <strong>{workspace}</strong>
                                                <span className="badge bg-secondary">{reports.length}</span>
                                            </div>
                                        </div>
                                        <AnimatePresence>
                                            {(expanded[workspace]) && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <div className="list-group list-group-flush">
                                                        {reports.map((report, indx) => (
                                                            <div
                                                                key={report.reportID}
                                                                className="list-group-item d-flex justify-content-between align-items-center"
                                                                style={{ backgroundColor: (selectedReport?.reportID == report.reportID) ? themeConfig.colors.selected : "" }}
                                                            >
                                                                <div className="d-flex flex-column">
                                                                    <div className="d-flex flex-row align-items-center gap-1">
                                                                        <AnimatePresence>
                                                                            {report.errorMsg && (
                                                                                <motion.div
                                                                                    key="error-indicator"
                                                                                    className="d-flex align-items-center gap-2"
                                                                                    initial={{ opacity: 0, x: 10 }}
                                                                                    animate={{ opacity: 1, x: 0 }}
                                                                                    exit={{ opacity: 0, x: 10 }}
                                                                                    transition={{ duration: 0.3 }}
                                                                                >
                                                                                    <TooltipButton
                                                                                        title={
                                                                                            <div className="d-flex flex-column align-items-start small" style={{ maxWidth: 240 }}>
                                                                                                <span className="fw-semibold text-warning mb-2">
                                                                                                    {report.errorMsg}
                                                                                                </span>
                                                                                                <span className="text-muted-theme">
                                                                                                    This report failed to load or was removed from its source. If it's available again in Power BI, you can click <strong>Retry</strong> to attempt reloading it into the system.
                                                                                                </span>
                                                                                                <button
                                                                                                    className="btn btn-sm btn-warning mt-2"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleRetry(report);
                                                                                                    }}
                                                                                                >
                                                                                                    {
                                                                                                        retryProgress ?
                                                                                                            <span className="d-flex flex-row align-items-center gap-1"><Spinner animation="border" size='sm' />Retrying</span> : "Retry"
                                                                                                    }
                                                                                                </button>
                                                                                            </div>
                                                                                        }
                                                                                    >
                                                                                        <MdErrorOutline size={18} className="text-warning" />
                                                                                    </TooltipButton>
                                                                                </motion.div>
                                                                            )}
                                                                        </AnimatePresence>

                                                                        <span className="fw-semibold">{report.reportName}</span>
                                                                    </div>
                                                                    <div className="d-flex flex-row align-items-center gap-2">
                                                                        <span className="text-muted-theme">
                                                                            Created at:&nbsp;
                                                                            {getMyLocalTime(report.createdAt)}
                                                                        </span>|
                                                                        <span className="text-muted-theme">
                                                                            Status:
                                                                        </span>
                                                                        <div className="form-check form-switch d-flex align-items-center gap-2">
                                                                            <TooltipButton title={(report.isActive == 1) ? "Disable this report" : "Enable this Report"}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="form-check-input j_rep_tog"
                                                                                    checked={(Math.abs(report.isActive) == 1)}
                                                                                    disabled={report.isActive < 0}
                                                                                    style={{ padding: "0px 20px", cursor: report.isActive < 0 ? "not-allowed" : "pointer" }}
                                                                                    onChange={() => {
                                                                                        setConfirmToggle(indx)
                                                                                    }}
                                                                                />
                                                                            </TooltipButton>
                                                                            {report.progress && (
                                                                                <Spinner animation="border" size="sm" />
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <ConfirmDialog
                                                                        show={confirmToggle == indx}
                                                                        confirmText={(report.isActive == 1) ? "Disable" : "Enable"}
                                                                        variant="danger"
                                                                        mode="modal"
                                                                        title={(report.isActive == 1) ? "Disable Report Confirmation" : "Enable Report Confirmation"}
                                                                        message={
                                                                            report.isActive == 1 ?
                                                                                report.isScheduled ?
                                                                                    <span>
                                                                                        This report has one or more scheduled distributions. Disabling it will also cancel all upcoming deliveries.<br />
                                                                                        <b>Are you sure you want to proceed?</b>
                                                                                    </span> :
                                                                                    "Are you sure you want to disable this report for Distribution?"
                                                                                : "Are you sure you want to enable this report for Distribution?"
                                                                        }
                                                                        onCancel={() => setConfirmToggle(null)}
                                                                        onConfirm={() => {
                                                                            if (report != null) {
                                                                                handleToggleStatus(indx, report)
                                                                            }
                                                                        }}
                                                                        loading={report.progress}
                                                                    />
                                                                </div>

                                                                <div className="d-flex flex-row align-items-center gap-1">
                                                                    <TooltipButton title={`Set Distribution`}>
                                                                        <button className='btn btn-outline-primary py-1 px-2 border-0 j_rep_dist' onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedReport(report);
                                                                        }}>
                                                                            <MdOutlineScheduleSend size={20} />
                                                                        </button>
                                                                    </TooltipButton>
                                                                    {/* <DeleteRowButton {...{
                                                                cell: report, row: report, deleteAPI: (r, cb) => {
                                                                    if (report != null) {
                                                                        DeleteReport(report.reportID, user.userID).then((x) => {
                                                                            cb();
                                                                            initReports();
                                                                        });
                                                                    }
                                                                }, nameFieldId: "reportName", title: "Report"
                                                            }} /> */}
                                                                    <div style={{ position: "relative", display: "inline-block" }}>
                                                                        <TooltipButton title="Copy Report Link">
                                                                            <button
                                                                                className="btn btn-outline-primary py-1 px-2 border-0 j_rep_copy"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleCopyLink(report);
                                                                                }}
                                                                            >
                                                                                <FaLink size={18} />
                                                                            </button>
                                                                        </TooltipButton>

                                                                        <AnimatePresence>
                                                                            {copiedID == report.reportID && (
                                                                                <motion.div
                                                                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                                                                    animate={{ opacity: 1, y: -36, scale: 1 }}
                                                                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                                                                    transition={{ duration: 0.3 }}
                                                                                    style={{
                                                                                        position: "absolute",
                                                                                        top: 0,
                                                                                        left: "-35px",
                                                                                        transform: "translateX(-50%)",
                                                                                        backgroundColor: "#198754", // Bootstrap success green
                                                                                        color: "#fff",
                                                                                        padding: "6px 12px",
                                                                                        fontSize: "12px",
                                                                                        borderRadius: "4px",
                                                                                        whiteSpace: "nowrap",
                                                                                        zIndex: 1000,
                                                                                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                                                                                    }}
                                                                                >
                                                                                    Copied!
                                                                                    <div
                                                                                        style={{
                                                                                            position: "absolute",
                                                                                            bottom: -6,
                                                                                            left: "56px",
                                                                                            transform: "translateX(-50%)",
                                                                                            width: 0,
                                                                                            height: 0,
                                                                                            borderLeft: "6px solid transparent",
                                                                                            borderRight: "6px solid transparent",
                                                                                            borderTop: "6px solid #198754",
                                                                                        }}
                                                                                    />
                                                                                </motion.div>
                                                                            )}
                                                                        </AnimatePresence>
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                        {
                                            selectedReport != null &&
                                            <SetDistributionModal
                                                show={selectedReport != null}
                                                onClose={() => { setSelectedReport(null) }}
                                                report={selectedReport}
                                            />
                                        }
                                    </ div>
                                ))
                    }
                </div>
            }
        />
    );
}
